import { Injectable, computed, inject, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter } from 'rxjs';
import {
  authRuntimeConfig,
  backendApiConfig,
  isApplicationConfigured,
  isBackendApiConfigured,
  isUnauthenticatedTestAccessEnabled,
} from './auth-config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private static readonly loginRedirectLockKey = 'nnapp.auth.loginRedirect.inProgress';
  private static readonly loginRedirectLockTtlMs = 5 * 60 * 1000;

  private readonly isAuthenticatedState = signal(false);
  private readonly accountNameState = signal<string | null>(null);
  private readonly accountIdState = signal<string | null>(null);
  private readonly isInitializingState = signal(!isUnauthenticatedTestAccessEnabled());
  private readonly rolesState = signal<readonly string[]>([]);

  readonly isConfigured = isApplicationConfigured();
  readonly isAuthenticated = this.isAuthenticatedState.asReadonly();
  readonly accountName = this.accountNameState.asReadonly();
  readonly accountId = this.accountIdState.asReadonly();
  readonly isInitializing = this.isInitializingState.asReadonly();
  readonly canViewRestaurantOrders = computed(
    () =>
      this.isAuthenticated() &&
      this.rolesState().some((role) => role === 'admin' || role === 'admin-restaurant'),
  );

  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  constructor() {
    this.warnIfMisconfigured();
    this.registerEventCallbacks();
  }

  login(redirectStartPage?: string): boolean {
    if (!this.isConfigured) {
      return false;
    }

    if (this.hasActiveLoginRedirectLock()) {
      return false;
    }

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(AuthService.loginRedirectLockKey, Date.now().toString());
    }

    const resolvedRedirectStartPage = redirectStartPage ?? this.getDefaultRedirectStartPage();

    this.msalService
      .loginRedirect({
        scopes: ['openid', 'profile', backendApiConfig.scope],
        redirectStartPage: resolvedRedirectStartPage,
      })
      .subscribe({
        error: (error: unknown) => {
          if (this.isInteractionInProgressError(error)) {
            return;
          }

          this.clearLoginRedirectLock();
          console.error('MSAL login redirect failed.', error);
        },
      });

    return true;
  }

  private getDefaultRedirectStartPage(): string {
    if (typeof window === 'undefined') {
      return '/restaurant';
    }

    return `${window.location.origin}/restaurant`;
  }

  logout(): void {
    const account = this.msalService.instance.getActiveAccount();
    this.msalService.logoutRedirect({
      account: account ?? undefined,
      postLogoutRedirectUri: authRuntimeConfig.postLogoutRedirectUri,
    });
  }

  private registerEventCallbacks(): void {
    this.msalService.instance.addEventCallback((message: EventMessage) => {
      if (message.eventType === EventType.LOGIN_SUCCESS && message.payload) {
        const result = message.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(result.account);
        this.clearLoginRedirectLock();
      }

      if (message.eventType === EventType.LOGOUT_SUCCESS) {
        this.rolesState.set([]);
        this.isAuthenticatedState.set(false);
        this.accountNameState.set(null);
        this.accountIdState.set(null);
      }
    });

    this.msalBroadcastService.inProgress$
      .pipe(filter((status) => status === InteractionStatus.None))
      .subscribe(() => this.syncAccountState());
  }

  private syncAccountState(): void {
    const account = this.getPreferredAccount();

    if (account) {
      this.msalService.instance.setActiveAccount(account);
      this.isAuthenticatedState.set(true);
      this.accountNameState.set(account.name ?? account.username ?? null);
      const accountId = this.resolveAccountId(account);
      this.accountIdState.set(accountId);
      const roles = account.idTokenClaims?.roles;
      this.rolesState.set(
        Array.isArray(roles)
          ? roles.filter((role): role is string => typeof role === 'string')
          : [],
      );

      this.isInitializingState.set(false);
      return;
    }

    this.isAuthenticatedState.set(false);
    this.rolesState.set([]);
    this.accountNameState.set(null);
    this.accountIdState.set(null);
    this.isInitializingState.set(false);
  }

  private getPreferredAccount(): AccountInfo | null {
    return (
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null
    );
  }

  hasRole(role: string): boolean {
    const account = this.getPreferredAccount();
    const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
    const roles = claims?.['roles'];
    return Array.isArray(roles) && roles.some((item) => typeof item === 'string' && item === role);
  }

  private warnIfMisconfigured(): void {
    const missingFields: string[] = [];

    if (!authRuntimeConfig.clientId) {
      missingFields.push('authClientId');
    }

    if (!authRuntimeConfig.tenantId) {
      missingFields.push('authTenantId');
    }

    if (!backendApiConfig.endpoint) {
      missingFields.push('backendApiEndpoint');
    }

    if (!backendApiConfig.scope) {
      missingFields.push('backendApiScope');
    }

    if (missingFields.length === 0) {
      return;
    }

    console.warn(
      `Auth/API configuration is missing values for: ${missingFields.join(', ')}. ` +
        'Update src/environments/environment.ts, src/environments/environment.development.ts, or src/environments/environment.production.ts before signing in.',
    );

    if (!isBackendApiConfigured()) {
      console.warn(
        'Backend API endpoint or scope is not configured; outgoing requests will not receive a bearer token.',
      );
    }
  }

  private resolveAccountId(account: AccountInfo): string | null {
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const objectId = claims?.['oid'];
    if (typeof objectId === 'string' && objectId.length > 0) {
      return objectId;
    }

    return account.localAccountId || account.homeAccountId || null;
  }

  private clearLoginRedirectLock(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.removeItem(AuthService.loginRedirectLockKey);
  }

  private hasActiveLoginRedirectLock(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const lockValue = window.sessionStorage.getItem(AuthService.loginRedirectLockKey);
    if (!lockValue) {
      return false;
    }

    const startedAt = Number(lockValue);
    if (!Number.isFinite(startedAt)) {
      this.clearLoginRedirectLock();
      return false;
    }

    const isFresh = Date.now() - startedAt < AuthService.loginRedirectLockTtlMs;
    if (!isFresh) {
      this.clearLoginRedirectLock();
    }

    return isFresh;
  }

  private isInteractionInProgressError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('errorCode' in error)) {
      return false;
    }

    const errorCode = (error as { errorCode?: unknown }).errorCode;
    return errorCode === 'interaction_in_progress';
  }
}
